// Account — who is signed in, which clients they can see, the devices signed
// in, and signing out of them. docs/CLIENT_PORTAL.md §4–§5.

import React, { useState } from 'react';
import { FwButton, Notice, Pill, RecordList, SectionLabel, Sheet } from '../../../ui/fw';
import { del, post, day } from '../portalApi';

const ACCESS = { billing: 'Invoices, payments and estimates', property: 'Estimates only' };

export default function Account({ me, onChanged, onSignedOut, onUnauthorized }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const company = me.company || {};

  const endOne = async (s) => {
    setError(null); setNote(null); setBusy(s.id);
    try {
      await del(`/portal/sessions/${s.id}`);
      setNote(`${s.device} is signed out.`);
      await onChanged();
    } catch (e) {
      if (e.status === 401) onUnauthorized(); else setError(e.message);
    } finally { setBusy(null); }
  };
  const endAll = async () => {
    setError(null); setBusy('all');
    try {
      await post('/portal/auth/logout-all');
      setConfirm(false);
      onSignedOut('You are signed out on every device.');
    } catch (e) {
      setBusy(null);
      if (e.status === 401) onUnauthorized(); else setError(e.message);
    }
  };

  const devices = me.sessions || [];
  return (
    <div className="pt-screen" data-screen="portal-account">
      <h1 className="pt-h1">Account</h1>
      {error ? <Notice tone="stop" data-note="error">{error}</Notice> : null}
      {note ? <Notice tone="ok" data-note="done">{note}</Notice> : null}

      <section className="pt-section">
        <SectionLabel>Signed in as</SectionLabel>
        <p className="pt-strong pt-wrap" data-field="email">{me.email}</p>
        <p className="pt-muted">There is no password. To sign in, enter this address on the sign-in page and we email you a link.</p>
      </section>

      <section className="pt-section">
        <SectionLabel>What you can see</SectionLabel>
        <RecordList
          columns={[
            { key: 'name', label: 'Client', role: 'title' },
            { key: 'access', label: 'Access', role: 'subtitle', render: (c) => ACCESS[c.access] || c.access },
          ]}
          rows={me.clients || []}
          caption="What you can see"
        />
        <p className="pt-muted">{`To change who can see what, contact ${company.name || 'us'}.`}</p>
      </section>

      <section className="pt-section">
        <SectionLabel>Signed in on</SectionLabel>
        <RecordList
          columns={[
            { key: 'device', label: 'Device', role: 'title' },
            { key: 'state', label: 'This device', role: 'badge', render: (s) => (s.current ? <Pill tone="ok">This device</Pill> : <Pill tone="neutral">Other device</Pill>) },
            { key: 'since', label: 'Signed in', render: (s) => day(s.signed_in_on) },
            { key: 'kept', label: 'Kept signed in', render: (s) => (s.remembered ? 'Yes, 30 days' : 'No') },
          ]}
          rows={devices}
          caption="Signed in on"
        />
        <div className="pt-list-actions">
          {devices.filter((s) => !s.current).map((s) => (
            <FwButton key={s.id} variant="ghost" loading={busy === s.id} onClick={() => endOne(s)} data-action="end-session" data-session={s.id}>
              {`Sign out ${s.device}, since ${day(s.signed_in_on)}`}
            </FwButton>
          ))}
        </div>
        <div className="pt-actions">
          <FwButton variant="stop" onClick={() => setConfirm(true)} data-action="sign-out-everywhere">Sign out everywhere</FwButton>
        </div>
      </section>

      {company.phone || company.email ? (
        <section className="pt-section">
          <SectionLabel>Questions about an invoice?</SectionLabel>
          <p className="pt-wrap">
            {company.name ? `${company.name}. ` : ''}
            {company.phone ? <a href={`tel:${company.phone}`}>{company.phone}</a> : null}
            {company.phone && company.email ? ' · ' : null}
            {company.email ? <a href={`mailto:${company.email}`}>{company.email}</a> : null}
          </p>
        </section>
      ) : null}

      <Sheet open={confirm} title="Sign out everywhere?" onClose={() => setConfirm(false)}
        footer={(
          <div className="pt-actions">
            <FwButton variant="stop" loading={busy === 'all'} onClick={endAll} data-action="confirm-sign-out-everywhere">Sign out everywhere</FwButton>
            <FwButton variant="ghost" onClick={() => setConfirm(false)}>Cancel</FwButton>
          </div>
        )}>
        <p>Every device signed in to this account is signed out, this one included. Any sign-in link not yet used stops working. You can sign in again at any time.</p>
      </Sheet>
    </div>
  );
}
